import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Star, ShieldCheck, GraduationCap, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const Stars = ({ value, onChange, size = "w-4 h-4", interactive = false }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <button key={i} type="button" disabled={!interactive} onClick={() => interactive && onChange(i)}
        className={interactive ? "cursor-pointer" : "cursor-default"} aria-label={`${i} star`}
        data-testid={interactive ? `rating-star-${i}` : undefined}>
        <Star className={`${size} ${i <= value ? "fill-taven-gold text-taven-gold" : "text-gray-300"}`} />
      </button>
    ))}
  </div>
);

export const Reviews = ({ kind, target }) => {
  const { user, authApi } = useAuth();
  const [data, setData] = useState({ reviews: [], count: 0, average: 0, distribution: {} });
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/reviews", { params: { kind, target } });
      setData(data);
    } catch { /* ignore */ }
  }, [kind, target]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) { toast.error("Please write a short review."); return; }
    setSubmitting(true);
    try {
      const { data } = await authApi.post("/reviews", { kind, target, rating, title, body });
      toast.success(data.message || "Review submitted for approval");
      setTitle(""); setBody(""); setRating(5);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review");
    } finally { setSubmitting(false); }
  };

  const verifiedLabel = kind === "course" ? "Verified Student" : "Verified Purchase";
  const VIcon = kind === "course" ? GraduationCap : ShieldCheck;

  return (
    <section className="mt-16" data-testid="reviews-section">
      <h2 className="text-2xl font-black font-display mb-6">Customer Reviews</h2>
      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        {/* summary */}
        <div className="border border-black/10 bg-white p-6 h-fit">
          <div className="text-center">
            <p className="text-5xl font-black">{data.average || "0.0"}</p>
            <Stars value={Math.round(data.average)} />
            <p className="text-xs text-muted-foreground mt-2" data-testid="reviews-count">{data.count} review{data.count === 1 ? "" : "s"}</p>
          </div>
          <div className="mt-5 space-y-1.5">
            {[5, 4, 3, 2, 1].map((s) => {
              const n = data.distribution?.[s] || 0;
              const pct = data.count ? Math.round((n / data.count) * 100) : 0;
              return (
                <div key={s} className="flex items-center gap-2 text-xs">
                  <span className="w-3">{s}</span>
                  <Star className="w-3 h-3 fill-taven-gold text-taven-gold" />
                  <div className="flex-1 h-2 bg-taven-gray"><div className="h-2 bg-taven-gold" style={{ width: `${pct}%` }} /></div>
                  <span className="w-6 text-right text-muted-foreground">{n}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* list + form */}
        <div>
          {user ? (
            <form onSubmit={submit} className="border border-black/10 bg-white p-6 mb-6" data-testid="review-form">
              <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3">Write a review</p>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-sm">Your rating:</span>
                <Stars value={rating} onChange={setRating} interactive size="w-6 h-6" />
              </div>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" data-testid="review-title" className="w-full border border-black/15 focus:border-taven-gold px-4 py-2.5 text-sm outline-none mb-3" />
              <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Share your experience…" data-testid="review-body" className="w-full border border-black/15 focus:border-taven-gold px-4 py-2.5 text-sm outline-none" />
              <p className="text-[11px] text-muted-foreground mt-2">Reviews are published after moderation. A <strong>{verifiedLabel}</strong> badge is added automatically when eligible.</p>
              <button disabled={submitting} data-testid="review-submit" className="mt-3 bg-taven-black text-white font-bold uppercase tracking-wider text-xs py-3 px-6 hover:bg-taven-orange transition-colors disabled:opacity-60 flex items-center gap-2">
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Submit Review
              </button>
            </form>
          ) : (
            <div className="border border-dashed border-black/20 bg-white p-6 mb-6 text-sm text-muted-foreground" data-testid="review-login-prompt">
              <Link to="/login" className="font-bold text-taven-gold hover:underline">Sign in</Link> to write a review. Only verified {kind === "course" ? "students" : "purchasers"} receive a verified badge.
            </div>
          )}

          {data.reviews.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="reviews-empty">No reviews yet. Be the first to share your experience.</p>
          ) : (
            <div className="space-y-4">
              {data.reviews.map((r, i) => (
                <div key={i} className="border border-black/10 bg-white p-5" data-testid="review-item">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Stars value={r.rating} />
                      {r.verified && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-green-700 bg-green-50 border border-green-200 px-2 py-0.5"><VIcon className="w-3 h-3" /> {verifiedLabel}</span>}
                    </div>
                    <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  {r.title && <p className="font-bold text-sm mt-2">{r.title}</p>}
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{r.body}</p>
                  <p className="text-xs font-semibold mt-2">— {r.author || "TAVEN Customer"}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Reviews;
